import React, { useState, useEffect } from 'react';
import { Plus, Book, ChevronRight, ChevronDown, Check, X, Trash2, GripVertical, UserPlus } from 'lucide-react';
import { Book as BookType, Chapter } from '../types';
import AssignChapterModal from './modals/AssignChapterModal';

interface ChapterSidebarProps {
  books: BookType[];
  chapters: Chapter[];
  selectedBookId: string | null;
  selectedChapterId: string | null;
  onCreateBook: () => void;
  onCreateChapter: (bookId: string) => void;
  onSelectBook: (bookId: string) => void;
  onSelectChapter: (chapterId: string) => void;
  onUpdateBookTitle: (bookId: string, newTitle: string) => void;
  onDeleteChapter?: (chapterId: string) => void;
  onReorderChapters: (bookId: string, chapters: Chapter[]) => void;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  chapterId: string;
}

interface DragState {
  isDragging: boolean;
  draggedChapterId: string | null;
  dragOverChapterId: string | null;
}

export default function ChapterSidebar({
  books,
  chapters,
  selectedBookId,
  selectedChapterId,
  onCreateBook,
  onCreateChapter,
  onSelectBook,
  onSelectChapter,
  onUpdateBookTitle,
  onDeleteChapter,
  onReorderChapters
}: ChapterSidebarProps) {
  const [expandedBooks, setExpandedBooks] = useState<Set<string>>(new Set());
  const [editingBookId, setEditingBookId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    chapterId: ''
  });
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedChapterId: null,
    dragOverChapterId: null
  });
  const [assignModalState, setAssignModalState] = useState<{
    isOpen: boolean;
    bookId: string;
    bookTitle: string;
    chapterId: string;
    chapterTitle: string;
  }>({
    isOpen: false,
    bookId: '',
    bookTitle: '',
    chapterId: '',
    chapterTitle: ''
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (contextMenu.visible) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu.visible]);

  const toggleBook = (bookId: string) => {
    const newExpanded = new Set(expandedBooks);
    if (newExpanded.has(bookId)) {
      newExpanded.delete(bookId);
    } else {
      newExpanded.add(bookId);
    }
    setExpandedBooks(newExpanded);
  };

  const startEditingBook = (book: BookType, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingBookId(book.id);
    setEditingTitle(book.title);
  };

  const saveBookTitle = (bookId: string) => {
    if (editingTitle.trim()) {
      onUpdateBookTitle(bookId, editingTitle.trim());
    }
    setEditingBookId(null);
  };

  const cancelEditing = () => {
    setEditingBookId(null);
    setEditingTitle('');
  };

  const handleContextMenu = (e: React.MouseEvent, chapterId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      chapterId
    });
  };

  const handleDeleteChapter = () => {
    if (onDeleteChapter && contextMenu.chapterId) {
      onDeleteChapter(contextMenu.chapterId);
    }
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const handleDragStart = (e: React.DragEvent, chapterId: string) => {
    e.dataTransfer.setData('text/plain', chapterId);
    setDragState({
      isDragging: true,
      draggedChapterId: chapterId,
      dragOverChapterId: null
    });
  };

  const handleDragOver = (e: React.DragEvent, chapterId: string) => {
    e.preventDefault();
    if (dragState.draggedChapterId !== chapterId) {
      setDragState(prev => ({
        ...prev,
        dragOverChapterId: chapterId
      }));
    }
  };

  const handleDrop = (e: React.DragEvent, bookId: string) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain');
    const dropTargetId = dragState.dragOverChapterId;

    if (draggedId && dropTargetId && draggedId !== dropTargetId) {
      const bookChapters = chapters.filter(c => c.book_id === bookId);
      const draggedIndex = bookChapters.findIndex(c => c.id === draggedId);
      const dropIndex = bookChapters.findIndex(c => c.id === dropTargetId);

      if (draggedIndex !== -1 && dropIndex !== -1) {
        const newChapters = [...bookChapters];
        const [draggedChapter] = newChapters.splice(draggedIndex, 1);
        newChapters.splice(dropIndex, 0, draggedChapter);

        // Update positions
        const updatedChapters = newChapters.map((chapter, index) => ({
          ...chapter,
          position: index
        }));

        onReorderChapters(bookId, updatedChapters);
      }
    }

    setDragState({
      isDragging: false,
      draggedChapterId: null,
      dragOverChapterId: null
    });
  };

  return (
    <aside className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">Books</h2>
          <button 
            onClick={onCreateBook}
            className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded"
            title="Create new book"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <nav className="space-y-2">
          {books.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              No books yet. Click the + button to create one.
            </div>
          ) : (
            books.map(book => {
              const bookChapters = chapters
                .filter(chapter => chapter.book_id === book.id)
                .sort((a, b) => a.position - b.position);
              const isExpanded = expandedBooks.has(book.id);
              const isEditing = editingBookId === book.id;
              
              return (
                <div key={book.id} className="space-y-1">
                  <div
                    className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors group ${
                      selectedBookId === book.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <button
                      onClick={() => toggleBook(book.id)}
                      className="p-1 hover:bg-gray-200 rounded mr-1"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <Book className="w-4 h-4 mr-2" />
                    {isEditing ? (
                      <div className="flex-1 flex items-center">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          className="flex-1 px-1 py-0.5 text-sm border rounded"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              saveBookTitle(book.id);
                            } else if (e.key === 'Escape') {
                              cancelEditing();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={() => saveBookTitle(book.id)}
                          className="p-1 hover:bg-gray-200 rounded ml-1"
                          title="Save"
                        >
                          <Check className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Cancel"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    ) : (
                      <span
                        className="flex-1 truncate"
                        onDoubleClick={(e) => startEditingBook(book, e)}
                        onClick={() => onSelectBook(book.id)}
                      >
                        {book.title}
                      </span>
                    )}
                    {!isEditing && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreateChapter(book.id);
                        }}
                        className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Add chapter"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {isExpanded && (
                    <div 
                      className="space-y-1"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, book.id)}
                    >
                      {bookChapters.map(chapter => (
                        <div
                          key={chapter.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, chapter.id)}
                          onDragOver={(e) => handleDragOver(e, chapter.id)}
                          onClick={() => onSelectChapter(chapter.id)}
                          onContextMenu={(e) => handleContextMenu(e, chapter.id)}
                          className={`p-2 rounded-lg cursor-pointer transition-colors ${
                            dragState.dragOverChapterId === chapter.id
                              ? 'border-t-2 border-blue-500'
                              : ''
                          } ${
                            selectedChapterId === chapter.id
                              ? 'bg-blue-50 text-blue-700'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
                            <div className="flex-1">
                              <div className="font-medium truncate">{chapter.title}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                Last modified: {new Date(chapter.lastModified).toLocaleDateString()}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignModalState({
                                  isOpen: true,
                                  bookId: book.id,
                                  bookTitle: book.title,
                                  chapterId: chapter.id,
                                  chapterTitle: chapter.title
                                });
                              }}
                              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full"
                              title="Assign to beta reader"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {bookChapters.length === 0 && (
                        <div className="text-sm text-gray-500 p-2">
                          No chapters yet
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="fixed bg-white rounded-lg shadow-lg py-1 border border-gray-200 z-50"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleDeleteChapter}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Chapter
          </button>
        </div>
      )}

      {/* Assignment Modal */}
      <AssignChapterModal
        isOpen={assignModalState.isOpen}
        onClose={() => setAssignModalState(prev => ({ ...prev, isOpen: false }))}
        bookId={assignModalState.bookId}
        bookTitle={assignModalState.bookTitle}
        chapterId={assignModalState.chapterId}
        chapterTitle={assignModalState.chapterTitle}
      />
    </aside>
  );
}